import React from 'react'

/**
 * useElementSize
 *
 * React hook that observes a DOM element's size using ResizeObserver and
 * returns its current width and height. Useful to synchronize heights between
 * two areas (e.g., make a sidebar match the main content height).
 */
export function useElementSize<T extends HTMLElement>(): [
  React.RefObject<T>,
  { width: number; height: number },
] {
  const elementRef = React.useRef<T | null>(null)
  const [size, setSize] = React.useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })

  React.useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // Initialize with current size
    const rect = element.getBoundingClientRect()
    setSize({ width: rect.width, height: rect.height })

    // Observe future size changes
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const cr = entry.contentRect
        setSize({ width: cr.width, height: cr.height })
      }
    })
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return [elementRef, size] as const
}
